/**
 * SafeFormDialog — Dialog modale bloccante per finestre di inserimento/modifica.
 *
 * Standard del gestionale:
 * - Il click sul backdrop NON chiude la finestra.
 * - Se la form è "dirty", ESC / X / Annulla mostrano una conferma "Modifiche non salvate".
 * - Focus trap + aria-modal ereditati da Radix Dialog.
 * - `Salva` è l'unico modo per chiudere senza conferma (parent chiama `onOpenChange(false)`).
 *
 * Utilizzo:
 *   <SafeFormDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     isDirty={isDirty}
 *     title="Modifica Azienda"
 *     description="…"
 *     size="lg"
 *   >
 *     ...form...
 *   </SafeFormDialog>
 */
import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const SIZE = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  "2xl": "max-w-5xl",
};

export function SafeFormDialog({
  open,
  onOpenChange,
  isDirty = false,
  title,
  description,
  size = "lg",
  showCloseButton = true,
  onAttemptClose,
  className,
  contentClassName,
  children,
  ...props
}) {
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const attemptClose = React.useCallback(() => {
    if (typeof onAttemptClose === "function") onAttemptClose();
    if (isDirty) {
      setConfirmOpen(true);
    } else {
      onOpenChange(false);
    }
  }, [isDirty, onOpenChange, onAttemptClose]);

  const forceClose = () => {
    setConfirmOpen(false);
    onOpenChange(false);
  };

  // Reset confirm dialog quando la dialog principale cambia stato
  React.useEffect(() => { if (!open) setConfirmOpen(false); }, [open]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => {
      // Radix chiama onOpenChange(false) solo quando la nostra logica lo permette;
      // ma catturo comunque il tentativo per sicurezza (es. Radix internals).
      if (v === false) {
        attemptClose();
      } else {
        onOpenChange(v);
      }
    }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          role="dialog"
          aria-modal="true"
          className={cn(
            "fixed left-[50%] top-[50%] z-50 flex flex-col w-full min-w-0",
            SIZE[size] || SIZE.lg,
            "translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white text-slate-900 p-6 shadow-2xl duration-200",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg",
            "max-h-[92vh] overflow-y-auto",
            contentClassName,
          )}
          // Nessuna chiusura da click esterno / interazione esterna
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          // v0.99.85+: ESC non chiude i popup di inserimento/modifica.
          // La chiusura avviene solo tramite X, Annulla o Salva con successo.
          onEscapeKeyDown={(e) => { e.preventDefault(); }}
          {...props}
        >
          {(title || description) && (
            <div className="flex flex-col space-y-1.5 pr-8" data-testid="safe-dialog-header">
              {title && (
                <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                  {title}
                </DialogPrimitive.Title>
              )}
              {description && (
                <DialogPrimitive.Description className="text-sm text-slate-500">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
          )}

          <div className={className}>{children}</div>

          {showCloseButton && (
            <button
              type="button"
              onClick={attemptClose}
              aria-label="Chiudi"
              className="absolute right-4 top-4 rounded-sm p-1 text-slate-500 opacity-80 hover:opacity-100 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-1"
              data-testid="safe-dialog-close-btn"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>

      {/* Conferma perdita modifiche non salvate */}
      <DialogPrimitive.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/60" />
          <DialogPrimitive.Content
            role="alertdialog"
            aria-modal="true"
            className="fixed left-[50%] top-[50%] z-[70] grid w-full max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-2xl sm:rounded-lg"
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            data-testid="unsaved-changes-dialog"
          >
            <div>
              <DialogPrimitive.Title className="text-lg font-semibold text-slate-900">
                Modifiche non salvate
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm text-slate-600 mt-2 leading-relaxed">
                Sono presenti modifiche non salvate.<br />
                Se esci ora tutte le modifiche inserite verranno perse.<br />
                <strong>Vuoi continuare?</strong>
              </DialogPrimitive.Description>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <Button
                type="button" variant="outline"
                onClick={forceClose}
                data-testid="unsaved-continue-discard-btn"
              >
                Continua senza salvare
              </Button>
              <Button
                type="button"
                autoFocus
                onClick={() => setConfirmOpen(false)}
                className="bg-emerald-800 hover:bg-emerald-700 text-white"
                data-testid="unsaved-back-to-edit-btn"
              >
                Torna alla modifica
              </Button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </DialogPrimitive.Root>
  );
}

export default SafeFormDialog;
